import React, { useState, useEffect, useRef } from 'react';

// Age confirmation for an alcohol producer. Deliberately not dismissible with
// Escape or a click outside — a choice has to be made — so it does not use the
// shared useDialog hook.
const STORAGE_KEY = 'winery_age_confirmed';
const REMEMBER_DAYS = 30;

// Where an under-21 visitor is sent. responsibility.org is the Foundation for
// Advancing Alcohol Responsibility, the conventional destination for this.
const EXIT_URL = 'https://www.responsibility.org';
const EXIT_LABEL = 'responsibility.org';

// Long enough to read the message and to undo a mis-tap.
const REDIRECT_SECONDS = 5;

function isStillValid() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return false;
    const confirmedAt = parseInt(stored, 10);
    if (Number.isNaN(confirmedAt)) return false;
    return Date.now() - confirmedAt < REMEMBER_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    // Private browsing can throw on localStorage; ask again rather than fail.
    return false;
  }
}

function AgeGate() {
  const [confirmed, setConfirmed] = useState(() => isStillValid());
  const [declined, setDeclined] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_SECONDS);
  const panelRef = useRef(null);
  const goBackRef = useRef(null);

  // Counts down, then leaves. Flipping `declined` back to false cancels the
  // pending timer through the cleanup, so a mis-tap costs nothing.
  useEffect(() => {
    if (!declined) return undefined;

    if (secondsLeft <= 0) {
      window.location.replace(EXIT_URL);
      return undefined;
    }

    const timer = setTimeout(() => setSecondsLeft(current => current - 1), 1000);
    return () => clearTimeout(timer);
  }, [declined, secondsLeft]);

  // The "No" button disappears when the message replaces it, which would drop
  // focus to the body. Move it to the way out instead.
  useEffect(() => {
    if (declined && goBackRef.current) goBackRef.current.focus();
  }, [declined]);

  useEffect(() => {
    if (confirmed) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Keep focus inside the gate: anything behind it is not meant to be reachable.
    const keepFocus = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        event.stopPropagation();
        const first = panelRef.current.querySelector('button');
        if (first) first.focus();
      }
    };
    document.addEventListener('focusin', keepFocus);

    const first = panelRef.current && panelRef.current.querySelector('button');
    if (first) first.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('focusin', keepFocus);
    };
  }, [confirmed]);

  if (confirmed) return null;

  const handleConfirm = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      // Not being able to remember is fine; they will just be asked again.
    }
    setConfirmed(true);
  };

  const handleDecline = () => {
    setSecondsLeft(REDIRECT_SECONDS);
    setDeclined(true);
  };

  const handleGoBack = () => {
    setDeclined(false);
    setSecondsLeft(REDIRECT_SECONDS);
  };

  return (
    <div
      className="age-gate-overlay fixed inset-0 flex items-center justify-center p-6"
      style={{ zIndex: 9998 }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="age-gate-title"
        className="bg-white rounded-2xl shadow-xl w-full text-center p-10"
        style={{ maxWidth: '30rem' }}
      >
        <img
          src="/logo-mark.png"
          alt=""
          aria-hidden="true"
          className="mx-auto mb-4"
          style={{ height: '4.5rem', width: 'auto' }}
        />
        <h1 id="age-gate-title" className="text-3xl font-bold text-wine mb-4">
          Vivat Alfa Winery
        </h1>

        {declined ? (
          <>
            {/* Announced once. The ticking number below is hidden from screen
                readers so it does not re-announce every second. */}
            <p role="status" className="text-lg text-gray-700 mb-2">
              Thanks for stopping by.
            </p>
            <p className="text-gray-600 mb-6">
              You must be 21 or older to view this site.
            </p>

            <p className="text-gray-600 mb-8" aria-hidden="true">
              Redirecting to {EXIT_LABEL} in{' '}
              <span className="font-bold text-wine">{secondsLeft}</span>…
            </p>

            <button
              ref={goBackRef}
              type="button"
              onClick={handleGoBack}
              className="px-8 py-4 text-lg rounded-lg transition bg-gray-200 hover:bg-gray-300 text-gray-800"
            >
              <span aria-hidden="true">←</span> Go back
            </button>

            <p className="text-sm text-gray-500 mt-6">
              Clicked that by mistake? Go back and answer again.
            </p>
          </>
        ) : (
          <>
            <p className="text-xl text-gray-700 mb-8">
              Are you 21 years of age or older?
            </p>

            <div className="flex gap-4 justify-center">
              <button
                type="button"
                onClick={handleConfirm}
                className="px-8 py-4 text-white text-lg font-bold rounded-lg transition hover:bg-wine-light"
                style={{ backgroundColor: '#722f37', border: '2px solid #f4bc57' }}
              >
                Yes, I am 21 or older
              </button>
              <button
                type="button"
                onClick={handleDecline}
                className="px-8 py-4 text-lg rounded-lg transition bg-gray-200 hover:bg-gray-300 text-gray-800"
              >
                No
              </button>
            </div>

            <p className="text-sm text-gray-500 mt-8">
              Please enjoy our wines responsibly.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default AgeGate;
